// ============================================================================
// LandingNav — the public marketing top bar. Sticky, translucent-on-scroll,
// mobile-first. Reuses the locked Pulse <Button> + <LanguageToggle>. The brand
// mark links home; the in-page anchors scroll to the feature / value sections;
// the trainer login is the clear, always-visible entry to /login.
// ============================================================================
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "../../components";
import { LanguageToggle } from "../../i18n/LanguageToggle";
import { CLIENT_LOGIN_PATH } from "../../client/routes";

function BrandMark() {
  const { t } = useTranslation();
  return (
    <Link to="/" className="group inline-flex items-center gap-2.5">
      <span className="grid h-9 w-9 place-items-center rounded-md bg-grad-coral shadow-volt">
        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
          <path
            d="M3 13h4l2-6 3 11 2.6-8L18 13h3"
            stroke="#fff"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="font-display text-lg font-extrabold tracking-tightest text-body">
        {t("app.name", { ns: "common" })}
      </span>
    </Link>
  );
}

export function LandingNav() {
  const { t } = useTranslation("landing");
  const reduce = useReducedMotion();

  return (
    <motion.header
      initial={reduce ? false : { y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 0.61, 0.36, 1] }}
      className="sticky top-0 z-40 border-b border-line/70 bg-paper/80 backdrop-blur-md supports-[backdrop-filter]:bg-paper/65"
    >
      <nav className="mx-auto flex max-w-shell items-center justify-between px-gutter py-3.5">
        <BrandMark />

        {/* In-page nav — hidden on the smallest screens, shown md+. */}
        <div className="hidden items-center gap-8 md:flex">
          <a
            href="#features"
            className="font-display text-sm font-bold text-body-soft transition-colors hover:text-body"
          >
            {t("nav.features")}
          </a>
          <a
            href="#portal"
            className="font-display text-sm font-bold text-body-soft transition-colors hover:text-body"
          >
            {t("nav.portal")}
          </a>
          <a
            href="#why"
            className="font-display text-sm font-bold text-body-soft transition-colors hover:text-body"
          >
            {t("nav.why")}
          </a>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <LanguageToggle className="hidden lg:inline-flex" />
          {/* TWO clear login doorways — client portal + trainer studio. */}
          <Link to={CLIENT_LOGIN_PATH} className="hidden sm:inline-flex">
            <Button size="sm" variant="ghost">
              {t("nav.clientSignIn")}
            </Button>
          </Link>
          <Link to="/login" className="hidden sm:inline-flex">
            <Button size="sm" variant="secondary">
              {t("nav.signIn")}
            </Button>
          </Link>
          <Link to="/signup">
            <Button size="sm">{t("nav.signUp")}</Button>
          </Link>
        </div>
      </nav>
    </motion.header>
  );
}
